import React from 'react';

/**
 * sections: { ENTRY_LONG:{root:{type:'group',op,children:[]}}, ... }
 * comparisons: [{id,type:'comparison',lhs,rhs,operator}]
 */
export default function SectionsEditor({ sections, setSections, comparisons }) {
  const allIds = comparisons.map(c => c.id);

  const updateSection = (key, patch) => {
    const sec = sections[key] || { root: { type:'group', op:'AND', children:[] } };
    const root = sec.root || { type:'group', op:'AND', children:[] };
    const next = { ...root, ...patch };
    setSections({ ...sections, [key]: { root: next } });
  };

  const addToSection = (sectionKey, cmpId) => {
    const sec = sections[sectionKey];
    const root = sec?.root || { type:'group', op:'AND', children:[] };
    if (root.children.includes(cmpId)) return; // already there
    updateSection(sectionKey, { children: [...root.children, cmpId] });
  };

  const removeFromSection = (sectionKey, cmpId) => {
    const sec = sections[sectionKey];
    const root = sec?.root || { type:'group', op:'AND', children:[] };
    updateSection(sectionKey, { children: root.children.filter(id => id !== cmpId) });
  };

  const move = (sectionKey, cmpId, dir) => {
    const sec = sections[sectionKey];
    const root = sec?.root || { type:'group', op:'AND', children:[] };
    const idx = root.children.indexOf(cmpId);
    if (idx === -1) return;
    const target = idx + dir;
    if (target < 0 || target >= root.children.length) return;
    const next = [...root.children];
    const [item] = next.splice(idx,1);
    next.splice(target,0,item);
    updateSection(sectionKey, { children: next });
  };

  const clearSection = (sectionKey) => updateSection(sectionKey, { children: [] });

  return (
    <div className="space-y-4 text-[11px]">
      {Object.entries(sections).map(([key, sec]) => {
        const root = (sec && sec.root) ? sec.root : { type:'group', op:'AND', children: [] };
        const included = root.children;
        const available = allIds.filter(id => !included.includes(id));
        return (
          <div key={key} className="border rounded-md p-2 bg-background/40 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-semibold tracking-wide">{key}</span>
              <div className="flex items-center gap-1">
                <select
                  value={root.op}
                  onChange={(e)=>updateSection(key,{ op: e.target.value })}
                  className="h-7 text-xs rounded border bg-background"
                >
                  <option value="AND">AND</option>
                  <option value="OR">OR</option>
                </select>
                {included.length > 0 && (
                  <button
                    type="button"
                    onClick={()=>clearSection(key)}
                    className="h-7 px-2 text-[10px] rounded border bg-muted/40 hover:bg-muted"
                  >Clear</button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="font-semibold mb-1">Included</div>
                {included.length === 0 && <div className="text-textSecondary italic">None</div>}
                {included.length > 0 && (
                  <ul className="space-y-1">
                    {included.map(id => (
                      <li key={id} className="flex items-center gap-1">
                        <span className="flex-1 px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/40 text-[10px]">cmp:{id}</span>
                        <div className="flex items-center gap-0.5">
                          <button
                            type="button"
                            onClick={()=>move(key,id,-1)}
                            className="px-1 py-0.5 rounded border bg-muted/40 hover:bg-muted"
                            title="Move up"
                          >↑</button>
                          <button
                            type="button"
                            onClick={()=>move(key,id,1)}
                            className="px-1 py-0.5 rounded border bg-muted/40 hover:bg-muted"
                            title="Move down"
                          >↓</button>
                          <button
                            type="button"
                            onClick={()=>removeFromSection(key,id)}
                            className="px-1 py-0.5 rounded border bg-red-500/20 text-red-600 hover:bg-red-500/30"
                            title="Remove"
                          >×</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="font-semibold mb-1">Available</div>
                {available.length === 0 && <div className="text-textSecondary italic">None</div>}
                {available.length > 0 && (
                  <ul className="flex flex-wrap gap-1">
                    {available.map(id => (
                      <li key={id}>
                        <button
                          type="button"
                          onClick={()=>addToSection(key,id)}
                          className="px-2 py-0.5 rounded border text-[10px] bg-muted/40 border-borderDefault hover:bg-muted/60"
                        >cmp:{id}</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
